import { getConfig } from '@leagueplatform/config';
import { captureError } from '@leagueplatform/observability';
import { getSalesforceChatToken } from './api/get-salesforce-chat-token';

type SalesforceSettings = {
  language?: string;
  // it's unclear what other settings are supported, so we allow any for now
  [key: string]: unknown;
};

/**
 * Hidden pre-chat fields passed directly to Salesforce via `setHiddenPrechatFields`.
 *
 * Values should be primitives (for example: strings, numbers, or booleans),
 * matching the Messaging for Web prechatAPI contract.
 */
export type HiddenPrechatFields = Record<string, unknown>;

type SalesforcePrechatConfig = {
  /**
   * Hidden pre-chat fields (not shown to the user) keyed by Salesforce
   * channel variable name.
   */
  hidden?: HiddenPrechatFields;
};

type SalesforceLiveChatConfig = {
  salesforceEmbeddedOrgId: string;
  salesforceEmbeddedDeploymentId: string;
  salesforceEmbeddedEndpointUrl: string;
  /**
   * The API name of the Enhanced Chat Embedded Service deployment used for
   * REST API calls (access-token, close-conversation).
   */
  esDeveloperName: string;
  settings?: SalesforceSettings;
  initOptions?: Record<string, unknown>;
  /**
   * Optional Salesforce pre-chat configuration that is applied via the
   * Salesforce `prechatAPI` when Messaging is ready.
   */
  prechat?: SalesforcePrechatConfig;
  chatType?: string;
};

export type SalesforceMessagingWindowState =
  | 'unknown'
  | 'closed'
  | 'minimized'
  | 'maximized';

/**
 * High-level, framework-agnostic view of Salesforce Messaging state.
 *
 * ## Two categories of fields
 *
 * **Salesforce-reported** (`isReady`, `unreadCount`, `windowState`):
 * Derived from Salesforce's window events documented at:
 * https://developer.salesforce.com/docs/service/messaging-web/guide/event-listeners.html
 *
 * **League-side guards** (`isChatBlocked`):
 * Set by League's SDK in response to internal verification failures — not
 * driven by any Salesforce event. These exist to protect consumers from
 * unsafe states that Salesforce itself has no visibility into (e.g. a failed
 * session clear before user verification).
 *
 * It intentionally avoids any React-specific concepts so it can be used from
 * any UI framework (or vanilla JS).
 *
 * NOTE: We intentionally do NOT expose a "sessionStatus" or "hasActiveConversation"
 * field because Salesforce does not provide a reliable way to query the current
 * session status on page load or refresh. Those values would be at best
 * event- and heuristic-based, which is too ambiguous for a public API.
 */
export type SalesforceMessagingState = {
  // --- Salesforce-reported fields ---

  /**
   * True after Salesforce dispatches `onEmbeddedMessagingReady`.
   */
  isReady: boolean;

  /**
   * Simple approximation of the unread message count for the current
   * conversation.
   *
   * This is derived from window + message events and is intentionally
   * conservative: it resets when the chat window is maximized or the
   * conversation is closed.
   */
  unreadCount: number;

  /**
   * Last-known high-level window state, derived from window lifecycle events.
   */
  windowState: SalesforceMessagingWindowState;

  // --- League-side guards ---

  /**
   * When `true`, the chat widget and any custom chat launcher UI should be
   * hidden and not accessible to the user.
   *
   * This is set by League's SDK — not by any Salesforce event — when an
   * internal safety check fails before user verification (e.g. the prior
   * user's Salesforce session could not be cleared). It exists to prevent
   * cross-user session exposure on shared devices.
   *
   * Consumers should treat this as an opaque "chat unavailable" signal and
   * show an appropriate fallback (e.g. a toast: "Chat is temporarily
   * unavailable").
   */
  isChatBlocked: boolean;
};

type SalesforceMessagingStateListener = (
  state: SalesforceMessagingState,
) => void;

const defaultMessagingState: SalesforceMessagingState = {
  isReady: false,
  unreadCount: 0,
  windowState: 'unknown',
  isChatBlocked: false,
};

let messagingState: SalesforceMessagingState = { ...defaultMessagingState };
const messagingStateListeners = new Set<SalesforceMessagingStateListener>();
let messagingStateInitialized = false;

const notifyMessagingStateListeners = () => {
  messagingStateListeners.forEach((listener) => listener(messagingState));
};

const updateMessagingState = (partial: Partial<SalesforceMessagingState>) => {
  messagingState = {
    ...messagingState,
    ...partial,
  };
  notifyMessagingStateListeners();
};

/**
 * Show or hide the Salesforce Messaging iframe that hosts the default
 * launcher and conversation UI.
 *
 * We deliberately target the top-level embedded messaging iframe by id
 * prefix, which aligns with Salesforce's documented DOM structure
 * (embeddedMessagingFrame).
 *
 * This helper is used to hide the default launcher/iframe when we want
 * integrators to rely solely on a custom CTA, and to re-show it when
 * the window is maximized or chat is opened.
 * As of January 2026 Salesforce Show/Hide Chat Button is unsupported/broken
 * As a work around, we hide the default launcher/iframe when we want
 * to hide the default chat button.
 */
const setSalesforceIframeVisible = (visible: boolean): void => {
  // Never reveal the iframe while chat is blocked (e.g. clearSession failed).
  // Hides are always allowed so the blocked state can be enforced by callers.
  if (visible && messagingState.isChatBlocked) {
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  const iframes = document.querySelectorAll<HTMLIFrameElement>(
    'iframe[id^="embeddedMessagingFrame"]',
  );

  iframes.forEach((iframe) => {
    // Empty string lets Salesforce / CSS decide the default display.
    // We avoid forcing a particular layout mode.
    // eslint-disable-next-line no-param-reassign
    iframe.style.display = visible ? '' : 'none';
  });
};

/**
 * Sets the `isChatBlocked` League-side guard in messaging state and
 * synchronises iframe visibility accordingly.
 *
 * Call with `true` when a safety check fails before user verification
 * (e.g. clearSession failed) to hide both the Salesforce iframe and any
 * custom launcher UI that reads from `SalesforceMessagingState`.
 */
export const setSalesforceChatBlocked = (blocked: boolean): void => {
  updateMessagingState({ isChatBlocked: blocked });
  setSalesforceIframeVisible(!blocked);
};

declare global {
  interface Window {
    embeddedservice_bootstrap?: {
      settings: {
        language?: string;
        [key: string]: unknown;
      };
      prechatAPI?: {
        setHiddenPrechatFields(
          hidden: HiddenPrechatFields,
        ): void | Promise<void>;
      };
      init(
        orgId: string,
        deploymentId: string,
        endpointUrl: string,
        initOptions?: Record<string, unknown>,
      ): void;
      userVerificationAPI?: {
        setIdentityToken(options: {
          identityTokenType: string;
          identityToken: string;
        }): void;
        clearSession(clearAllTabsAndWindows: boolean): Promise<void>;
      };
      utilAPI?: {
        launchChat(): void;
        hideChatButton(): void;
      };
    };
    leagueSalesforceMessagingScriptLoaded?: boolean;
    leagueSalesforceMessagingInitialized?: boolean;
    leagueSalesforceMessagingReady?: boolean;
  }
}

/**
 * Salesforce Embedded Messaging event names.
 *
 * Use these constants when listening to Salesforce lifecycle events with `onSalesforceEvent()`.
 *
 * @see https://developer.salesforce.com/docs/service/messaging-web/guide/event-listeners.html
 */
export const SALESFORCE_EVENTS = {
  /** Fires when the Salesforce JavaScript API is fully initialized and ready to use */
  READY: 'onEmbeddedMessagingReady',
  /** Fires when the user verification identity token has expired */
  IDENTITY_TOKEN_EXPIRED: 'onEmbeddedMessagingIdentityTokenExpired',
  /** Fires when a page is loaded during business hours */
  WITHIN_BUSINESS_HOURS: 'onEmbeddedMessagingWithinBusinessHours',
  /** Fires when the current time transitions into business hours */
  BUSINESS_HOURS_STARTED: 'onEmbeddedMessagingBusinessHoursStarted',
  /** Fires when the current time transitions out of business hours */
  BUSINESS_HOURS_ENDED: 'onEmbeddedMessagingBusinessHoursEnded',
  /** Fires when a messaging conversation starts (current tab only) */
  CONVERSATION_STARTED: 'onEmbeddedMessagingConversationStarted',
  /** Fires when a messaging conversation is loaded into a browser tab */
  CONVERSATION_OPENED: 'onEmbeddedMessagingConversationOpened',
  /**
   * Fires when a messaging conversation ends.
   *
   * Note: Salesforce's official docs describe this as firing when "an end user
   * closes the messaging conversation", but observed behavior is that it fires
   * whenever the conversation ends — including when an agent ends it or the
   * user clicks "End Chat" inside the widget. The widget UI may remain visually
   * open in a post-chat state after this event fires.
   */
  CONVERSATION_CLOSED: 'onEmbeddedMessagingConversationClosed',
  /** Fires when the messaging window is minimized */
  WINDOW_MINIMIZED: 'onEmbeddedMessagingWindowMinimized',
  /** Fires when the messaging window is maximized */
  WINDOW_MAXIMIZED: 'onEmbeddedMessagingWindowMaximized',
  /** Fires when the messaging window is closed */
  WINDOW_CLOSED: 'onEmbeddedMessagingWindowClosed',
  /** Fires when a delivery receipt is received (if enabled) */
  MESSAGE_DELIVERED: 'onEmbeddedMessageDelivered',
  /** Fires when a read receipt is received (if enabled) */
  MESSAGE_READ: 'onEmbeddedMessageRead',
  /** Fires when a rep, bot, or end user sends a message */
  MESSAGE_SENT: 'onEmbeddedMessageSent',
  /** Fires when the standard Salesforce chat button is created */
  BUTTON_CREATED: 'onEmbeddedMessagingButtonCreated',
  /** Fires when the session status changes (e.g. Active → Ended) */
  SESSION_STATUS_UPDATE: 'onEmbeddedMessagingSessionStatusUpdate',
} as const;

const getSalesforceConfig = (): SalesforceLiveChatConfig | null => {
  const { config } = getConfig();
  const sfConfig = config.salesforceLiveChat as
    | SalesforceLiveChatConfig
    | undefined;

  if (
    !sfConfig ||
    !sfConfig.salesforceEmbeddedOrgId ||
    !sfConfig.salesforceEmbeddedDeploymentId ||
    !sfConfig.salesforceEmbeddedEndpointUrl
  ) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[Salesforce Live Chat] `config.salesforceLiveChat` is missing or incomplete. ' +
          'Salesforce Messaging script will not be loaded. ' +
          'Expected: `salesforceEmbeddedOrgId`, `salesforceEmbeddedDeploymentId`, `salesforceEmbeddedEndpointUrl`.',
      );
    }
    return null;
  }

  return sfConfig;
};

let runtimeHiddenPrechatFields: HiddenPrechatFields | undefined;

/**
 * Tracks conversation IDs for which a close request is already in-flight,
 * preventing duplicate REST API calls from rapid SESSION_STATUS_UPDATE events.
 */
const closingConversationIds = new Set<string>();

/**
 * Apply configured and runtime hidden pre-chat fields to the Salesforce prechatAPI.
 *
 * This function is safe to call multiple times and before the Salesforce
 * `prechatAPI` is available; in those cases it will simply no-op.
 *
 * When `forceHidden` is true the Salesforce setter is called even if the
 * merged field map is empty, allowing callers to clear previously applied
 * fields (e.g. via `replaceHidden: true` with an empty object).
 */
const applyPrechatFields = (options?: { forceHidden?: boolean }): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const prechatAPI = window.embeddedservice_bootstrap?.prechatAPI;
  if (!prechatAPI) {
    return;
  }

  const baseConfig = getSalesforceConfig();
  const baseHidden = baseConfig?.prechat?.hidden ?? {};

  const hidden: HiddenPrechatFields = {
    ...baseHidden,
    ...(runtimeHiddenPrechatFields ?? {}),
  };

  if (Object.keys(hidden).length > 0 || options?.forceHidden) {
    prechatAPI.setHiddenPrechatFields(hidden);
  }
};

export const loadSalesforceMessagingScript = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    // No-op on server
    return;
  }

  if (window.leagueSalesforceMessagingScriptLoaded) {
    return;
  }

  const config = getSalesforceConfig();
  if (!config) {
    return;
  }

  const scriptUrl = `${config.salesforceEmbeddedEndpointUrl}/assets/js/bootstrap.min.js`;

  await new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${scriptUrl}"]`,
    );
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        window.leagueSalesforceMessagingScriptLoaded = true;
        resolve();
        return;
      }
      existing.addEventListener('load', () => {
        window.leagueSalesforceMessagingScriptLoaded = true;
        resolve();
      });
      existing.addEventListener('error', (err) => {
        window.leagueSalesforceMessagingScriptLoaded = false;
        reject(err);
      });
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = scriptUrl;
    script.async = true;
    script.dataset.loaded = 'false';

    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      window.leagueSalesforceMessagingScriptLoaded = true;
      resolve();
    });

    script.addEventListener('error', (err) => {
      window.leagueSalesforceMessagingScriptLoaded = false;
      reject(err);
    });

    document.body.appendChild(script);
  });
};

/**
 * Listen to Salesforce Messaging lifecycle events.
 *
 * Internal helper — wraps window.addEventListener / removeEventListener
 * for Salesforce event names.
 */
export const onSalesforceEvent = (
  eventName: string,
  handler: (event: Event) => void,
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  window.addEventListener(eventName, handler);

  return () => {
    window.removeEventListener(eventName, handler);
  };
};

/**
 * Set or merge Salesforce hidden pre-chat fields at runtime.
 *
 * This allows integrators to provide pre-chat context (like member profile
 * data) in addition to any configuration-based pre-chat fields.
 *
 * By default, subsequent calls **merge** additional values on top of
 * previously provided runtime fields (shallow merge; newer keys win).
 * To **replace** all runtime fields instead of merging, pass
 * `replaceHidden: true`. When set, the runtime field map is overwritten
 * entirely with the values from the current call.
 *
 * This function is safe to call before Salesforce Messaging is fully ready;
 * pre-chat fields will be applied when the `prechatAPI` becomes available.
 */
export const setSalesforcePrechatFields = (fields: {
  hidden?: HiddenPrechatFields;
  /** When true, replaces all runtime hidden fields instead of merging. */
  replaceHidden?: boolean;
}): void => {
  if (typeof window === 'undefined') {
    // No-op on server to avoid leaking runtime prechat state across requests.
    return;
  }

  if (fields.hidden) {
    runtimeHiddenPrechatFields = fields.replaceHidden
      ? fields.hidden
      : {
          ...(runtimeHiddenPrechatFields ?? {}),
          ...fields.hidden,
        };
  }

  applyPrechatFields({
    forceHidden: fields.replaceHidden,
  });
};

/**
 * Lazily initialize the global messaging state listeners.
 *
 * We only attach window event listeners when:
 * - running in a browser, AND
 * - someone has asked for messaging state (get or subscribe).
 */
const ensureMessagingStateInitialized = (): void => {
  if (messagingStateInitialized) {
    return;
  }

  if (typeof window === 'undefined') {
    // SSR / non-browser: keep default state and do not attach listeners.
    return;
  }

  messagingStateInitialized = true;

  // READY → mark API as ready.
  onSalesforceEvent(SALESFORCE_EVENTS.READY, () => {
    updateMessagingState({
      isReady: true,
    });
  });

  // Conversation lifecycle.
  onSalesforceEvent(SALESFORCE_EVENTS.CONVERSATION_STARTED, () => {
    updateMessagingState({
      // When a new conversation starts, reset unread count.
      unreadCount: 0,
    });
  });

  onSalesforceEvent(SALESFORCE_EVENTS.CONVERSATION_CLOSED, () => {
    // Conversation lifecycle event — only reset unread count.
    // Window state is managed exclusively by WINDOW_MAXIMIZED,
    // WINDOW_MINIMIZED, and WINDOW_CLOSED.
    updateMessagingState({
      unreadCount: 0,
    });
  });

  // Window lifecycle — used both for UX and for approximating unread counts.
  //
  // NOTE: iframe visibility (setSalesforceIframeVisible) is managed separately
  // in initSalesforceMessaging so that the OOTB button stays hidden even when
  // no consumer subscribes to messaging state (e.g. via useSalesforceChatCta).
  onSalesforceEvent(SALESFORCE_EVENTS.WINDOW_MAXIMIZED, () => {
    updateMessagingState({
      windowState: 'maximized',
      // Treat maximizing the window as "user has seen unread messages".
      unreadCount: 0,
    });
  });

  onSalesforceEvent(SALESFORCE_EVENTS.WINDOW_MINIMIZED, () => {
    updateMessagingState({
      windowState: 'minimized',
    });
  });

  onSalesforceEvent(SALESFORCE_EVENTS.WINDOW_CLOSED, () => {
    updateMessagingState({
      windowState: 'closed',
      unreadCount: 0,
    });
  });

  // Message sent events (rep, bot, or end user).
  //
  // We use this as the primary signal to approximate unread counts. The event
  // payload corresponds to the CONVERSATION_MESSAGE SSE documented at:
  // https://developer.salesforce.com/docs/service/messaging-api/references/about/server-sent-events-structure.html#conversation_message
  //
  // Because the exact payload shape and sender-role fields can vary between
  // orgs and Salesforce versions, we apply a defensive heuristic:
  // - Try to infer whether the message was sent *by the end user* based on
  //   known/likely role or author-type fields.
  // - Only increment unreadCount for messages that are *not obviously* from
  //   the end user (i.e., likely from an agent or bot) while the window is
  //   not maximized.
  onSalesforceEvent(SALESFORCE_EVENTS.MESSAGE_SENT, (event) => {
    const customEvent = event as CustomEvent<{
      payload?: {
        message?: {
          sender?: { role?: string; type?: string };
          authorType?: string;
        };
        sender?: { role?: string; type?: string };
        authorType?: string;
      };
      sender?: { role?: string; type?: string };
      authorType?: string;
    }>;

    const detail = customEvent?.detail;
    const payload = detail?.payload;

    // Best-effort extraction of a "role"/"author type" signal from the payload.
    const roleCandidates: Array<string | undefined> = [
      payload?.message?.sender?.role,
      payload?.message?.sender?.type,
      payload?.sender?.role,
      payload?.sender?.type,
      payload?.message?.authorType,
      payload?.authorType,
      detail?.authorType,
    ];

    const rawRole =
      roleCandidates.find((value) => typeof value === 'string') ?? '';

    const normalizedRole = rawRole.toLowerCase();

    // Common external/end-user role labels seen in various Salesforce payloads.
    const isFromEndUser =
      normalizedRole === 'enduser' ||
      normalizedRole === 'end_user' ||
      normalizedRole === 'customer' ||
      normalizedRole === 'end user';

    // If we can confidently infer that the message came from the end user,
    // do not treat it as "unread".
    if (isFromEndUser) {
      return;
    }

    // Otherwise, treat it as an inbound message (from agent/bot) and
    // increment unread count when the window is not maximized.
    if (messagingState.windowState !== 'maximized') {
      updateMessagingState({
        unreadCount: messagingState.unreadCount + 1,
      });
    }
  });
};

/**
 * Get the current Salesforce Messaging state.
 *
 * Safe to call from any framework. In non-browser environments (SSR), this
 * returns a stable default state and does not attach any listeners.
 */
export const getSalesforceMessagingState = (): SalesforceMessagingState => {
  ensureMessagingStateInitialized();
  return messagingState;
};

/**
 * Subscribe to Salesforce Messaging state changes.
 *
 * The provided listener is called immediately with the current state and then
 * on every subsequent change. Returns an unsubscribe function.
 *
 * This API can be used to derive UI behavior such as:
 *
 * - Displaying an unread badge
 * - Reacting to session status changes
 */
export const subscribeToSalesforceMessagingState = (
  listener: SalesforceMessagingStateListener,
): (() => void) => {
  ensureMessagingStateInitialized();

  messagingStateListeners.add(listener);

  // Immediately provide the current snapshot.
  listener(messagingState);

  return () => {
    messagingStateListeners.delete(listener);
  };
};

/**
 * Optional identity token provider for Salesforce REST API calls (e.g. closeSalesforceConversation).
 * Set automatically by useSalesforceUserVerification when options.getToken is provided,
 * so the same token source is used for both identity verification and REST API calls.
 */
let salesforceChatTokenProvider: (() => Promise<string>) | undefined;

/**
 * Registers an identity token provider for Salesforce REST API calls.
 *
 * This is called internally by `useSalesforceUserVerification` when a custom
 * `getToken` option is supplied, so that `closeSalesforceConversation()` uses
 * the same token source as the identity verification flow.
 *
 * Host apps that manage their own JWT lifecycle (e.g. B2B bridge apps) do not
 * need to call this directly — mounting `useSalesforceUserVerification` with
 * `options.getToken` is sufficient to cover both paths.
 */
export const setSalesforceChatTokenProvider = (
  getToken: (() => Promise<string>) | undefined,
): void => {
  salesforceChatTokenProvider = getToken;
};

/**
 * Obtain a short-lived Salesforce REST API access token by exchanging the
 * current user's limited-scope Salesforce chat token.
 *
 * This calls the Enhanced Chat "generate access token for authenticated user"
 * endpoint. The returned token is used as a Bearer token for subsequent REST
 * API calls (e.g. closeConversation).
 *
 * @returns The Salesforce access token string.
 * @throws If config is incomplete, the identity token cannot be obtained, or
 *         the Salesforce endpoint returns an error.
 */
const getSalesforceRestAccessToken = async (): Promise<string> => {
  const config = getSalesforceConfig();
  if (!config) {
    throw new Error(
      'Salesforce REST API config incomplete: salesforceLiveChat config is missing.',
    );
  }

  const scrtUrl = config.initOptions?.scrt2URL as string | undefined;
  if (!scrtUrl) {
    throw new Error(
      'Salesforce REST API config incomplete: initOptions.scrt2URL is required.',
    );
  }

  const { salesforceEmbeddedOrgId: orgId, esDeveloperName } = config;

  const customerIdentityToken = salesforceChatTokenProvider
    ? await salesforceChatTokenProvider()
    : await getSalesforceChatToken();

  const response = await fetch(
    `${scrtUrl}/iamessage/api/v2/authorization/authenticated/access-token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orgId,
        esDeveloperName,
        capabilitiesVersion: '1',
        platform: 'Web',
        authorizationType: 'JWT',
        customerIdentityToken,
      }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Salesforce access-token request failed: ${response.status}`,
    );
  }

  const data = (await response.json()) as { accessToken?: string };
  if (!data.accessToken) {
    throw new Error('Salesforce access-token response missing accessToken.');
  }

  return data.accessToken;
};

/**
 * Close a Salesforce conversation via the Enhanced Chat REST API.
 *
 * This is a best-effort operation: errors are caught and logged but never
 * thrown to callers. It is safe to fire-and-forget.
 *
 * The function is automatically called when a SESSION_STATUS_UPDATE event
 * reports `sessionStatus: 'Ended'`, but can also be called manually if
 * needed.
 *
 * @param conversationId - The UUID of the conversation to close (lowercase).
 */
export const closeSalesforceConversation = async (
  conversationId: string,
): Promise<void> => {
  if (closingConversationIds.has(conversationId)) {
    return;
  }
  closingConversationIds.add(conversationId);

  try {
    const config = getSalesforceConfig();
    if (!config) {
      return;
    }

    const scrtUrl = config.initOptions?.scrt2URL as string | undefined;
    if (!scrtUrl) {
      return;
    }

    const accessToken = await getSalesforceRestAccessToken();

    const response = await fetch(
      `${scrtUrl}/iamessage/api/v2/conversation/${encodeURIComponent(
        conversationId,
      )}?esDeveloperName=${encodeURIComponent(config.esDeveloperName)}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (!response.ok) {
      captureError(
        new Error(
          `Salesforce closeConversation failed with status ${response.status}`,
        ),
        { errorName: 'Salesforce Close Conversation HTTP Error' },
      );
    }
  } catch (error) {
    // Best-effort: capture but never propagate errors to avoid disrupting UX.
    captureError(
      error instanceof Error
        ? error
        : new Error(
            `Failed to close Salesforce conversation: ${String(error)}`,
          ),
      { errorName: 'Salesforce Close Conversation Failure' },
    );
  } finally {
    closingConversationIds.delete(conversationId);
  }
};

/**
 * Initialize Salesforce Messaging for Web (Enhanced Chat).
 *
 * This function loads the Salesforce bootstrap script and initializes the
 * `embeddedservice_bootstrap` API. It reads configuration from
 * `config.salesforceLiveChat`.
 *
 * **When to call:**
 * - **Eager loading (app boot)**: Call in your app's root component if chat
 *   is a core feature and you want it available immediately.
 * - **Lazy loading (on-demand)**: Call when the user navigates to a support
 *   section or clicks a help button to optimize initial load time.
 *
 * **Idempotency**: Safe to call multiple times; subsequent calls are no-ops.
 *
 * @returns Promise that resolves when Salesforce Messaging is initialized
 *          and ready to use.
 *
 * @example
 * // Standard usage (reads from config.salesforceLiveChat)
 * import { initSalesforceMessaging } from '@leagueplatform/salesforce-live-chat';
 *
 * // In App.tsx or similar bootstrap
 * useEffect(() => {
 *   initSalesforceMessaging().catch(err => {
 *     console.error('Salesforce chat failed to initialize:', err);
 *   });
 * }, []);
 */
export const initSalesforceMessaging = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    // No-op on server
    return;
  }

  if (window.leagueSalesforceMessagingInitialized) {
    return;
  }

  const config = getSalesforceConfig();
  if (!config) {
    return;
  }

  if (process.env.NODE_ENV !== 'production') {
    // Provide integrator breadcrumbs for common misconfigurations that
    // result in the launcher not appearing without obvious errors.
    // TODO: revisit messaging around "Messaging launcher" once we disable the OOTB chat button on launch
    if (!config.settings?.language) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Salesforce Live Chat] No `salesforceLiveChat.settings.language` configured. ' +
          'Salesforce will fall back to its own default language. ' +
          'If the Messaging launcher does not appear, confirm language and domain configuration with your Salesforce administrator.',
      );
    }

    if (!config.initOptions?.scrt2URL) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Salesforce Live Chat] No `salesforceLiveChat.initOptions.scrt2URL` configured. ' +
          'Some Salesforce Messaging deployments require `scrt2URL` for secure or cross-domain routing. ' +
          'If the launcher does not appear or fails to connect, verify `scrt2URL` with your Salesforce administrator.',
      );
    }
  }

  await loadSalesforceMessagingScript();

  if (!window.embeddedservice_bootstrap) {
    return;
  }

  // Apply settings to embeddedservice_bootstrap.settings before calling init()
  const { settings } = config;

  if (settings && Object.keys(settings).length > 0) {
    window.embeddedservice_bootstrap.settings =
      window.embeddedservice_bootstrap.settings || {};

    Object.assign(window.embeddedservice_bootstrap.settings, settings);
  }

  // Always hide the standard Salesforce chat button in favor of custom launchers.
  // This complements `hideChatButtonOnLoad` by also hiding the button whenever
  // Salesforce creates it in the page lifecycle.
  onSalesforceEvent(SALESFORCE_EVENTS.BUTTON_CREATED, () => {
    // As of January 2026 Salesforce Show/Hide Chat Button is unsupported/broken
    // We will leave this here for now, but it is not effective.
    window.embeddedservice_bootstrap?.utilAPI?.hideChatButton?.();
    // As a work around, we hide the iframe so the default launcher surface is not visible;
    // all integrators are expected to use a custom CTA instead.
    setSalesforceIframeVisible(false);
  });

  // Manage iframe visibility across the Salesforce window lifecycle so the
  // OOTB button stays hidden regardless of whether the consuming app uses
  // useSalesforceChatCta / subscribes to messaging state.
  onSalesforceEvent(SALESFORCE_EVENTS.WINDOW_MAXIMIZED, () => {
    setSalesforceIframeVisible(true);
  });

  onSalesforceEvent(SALESFORCE_EVENTS.WINDOW_MINIMIZED, () => {
    setSalesforceIframeVisible(false);
  });

  onSalesforceEvent(SALESFORCE_EVENTS.WINDOW_CLOSED, () => {
    setSalesforceIframeVisible(false);
  });

  // Listen for session status changes to auto-close conversations via REST API.
  // The embedded SDK does not expose a closeConversation method, so we call
  // the Enhanced Chat REST API directly when the session ends.
  onSalesforceEvent(SALESFORCE_EVENTS.SESSION_STATUS_UPDATE, (event) => {
    try {
      const detail = (event as CustomEvent)?.detail;
      const entryPayload = detail?.conversationEntry?.entryPayload;
      const parsed =
        typeof entryPayload === 'string' ? JSON.parse(entryPayload) : null;

      if (parsed?.sessionStatus === 'Ended' && parsed?.conversationIdentifier) {
        closeSalesforceConversation(parsed.conversationIdentifier);
      }
    } catch (error) {
      // Defensive: if the payload shape changes, don't break messaging state.
      captureError(
        error instanceof Error
          ? error
          : new Error('Failed to parse SESSION_STATUS_UPDATE payload'),
        { errorName: 'Salesforce Session Status Update Parse Error' },
      );
    }
  });

  // Listen for Salesforce ready event and set ready flag.
  // Duplicate listeners before READY fires are tolerated; we rely on the init singleton guard above (window.leagueSalesforceMessagingInitialized).
  if (!window.leagueSalesforceMessagingReady) {
    onSalesforceEvent(SALESFORCE_EVENTS.READY, () => {
      window.leagueSalesforceMessagingReady = true;
      applyPrechatFields();
    });
  }

  // Initialize Salesforce with initOptions
  window.embeddedservice_bootstrap.init(
    config.salesforceEmbeddedOrgId,
    config.salesforceEmbeddedDeploymentId,
    config.salesforceEmbeddedEndpointUrl,
    config.initOptions,
  );

  window.leagueSalesforceMessagingInitialized = true;
};

/**
 * Programmatically open the Salesforce chat window.
 *
 * This function launches the Salesforce Messaging chat client, allowing you to
 * integrate a custom chat button instead of using Salesforce's default button.
 *
 * **Requirements:**
 * - Must be called AFTER `initSalesforceMessaging()` has completed
 * - Must wait for the 'onEmbeddedMessagingReady' event before calling
 *
 * **Idempotency:** Safe to call multiple times; Salesforce handles duplicate calls.
 *
 * @throws {Error} If Salesforce Messaging is not ready or utilAPI is unavailable
 *
 * @example
 * // Basic usage with custom button
 * import { initSalesforceMessaging, openSalesforceChat } from '@leagueplatform/salesforce-live-chat';
 *
 * await initSalesforceMessaging();
 * // Wait for ready event, then enable your button
 *
 * <button onClick={openSalesforceChat}>Chat with Support</button>
 *
 * @example
 * // Use useSalesforceChatCta hook for ready-gated chat button
 * import { useSalesforceChatCta } from '@leagueplatform/salesforce-live-chat';
 *
 * const { isVisible, openChat } = useSalesforceChatCta();
 *
 * {isVisible && <button onClick={openChat}>Chat Now</button>}
 */
export const openSalesforceChat = (): void => {
  if (typeof window === 'undefined') {
    // No-op on server
    return;
  }

  // Ensure the iframe is visible before attempting to launch chat, in case
  // it was previously hidden while minimized or closed.
  setSalesforceIframeVisible(true);

  if (!window.leagueSalesforceMessagingReady) {
    throw new Error(
      'Salesforce Messaging not ready. Wait for the ready event before calling openSalesforceChat().',
    );
  }

  if (!window.embeddedservice_bootstrap?.utilAPI?.launchChat) {
    throw new Error(
      'Salesforce utilAPI.launchChat is not available. Ensure the Salesforce script loaded correctly.',
    );
  }

  window.embeddedservice_bootstrap.utilAPI.launchChat();
};

/**
 * Identity token payload for Salesforce User Verification.
 *
 * @see https://developer.salesforce.com/docs/service/messaging-web/guide/user-verification.html
 */
export type SalesforceIdentityToken = {
  /** Type of identity token, typically "JWT" */
  identityTokenType: string;
  /** The signed identity token (for example, a JWT) */
  identityToken: string;
};

/**
 * Send a user verification identity token to Salesforce.
 *
 * Call this AFTER receiving the `onEmbeddedMessagingReady` event.
 * You must call this in every tab or window where you want the user to have
 * an authenticated conversation, and again after each page reload.
 *
 * @throws {Error} If the Salesforce User Verification API is not available.
 *
 * @example
 * // Typically called via the useSalesforceUserVerification hook.
 * setSalesforceIdentityToken({
 *   identityTokenType: 'JWT',
 *   identityToken: customerSignedJwt,
 * });
 */
export const setSalesforceIdentityToken = (
  token: SalesforceIdentityToken,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const userVerificationAPI =
    window.embeddedservice_bootstrap?.userVerificationAPI;

  if (!userVerificationAPI?.setIdentityToken) {
    throw new Error(
      'Salesforce User Verification API is not available. Ensure Messaging is initialized and userVerification is enabled in your deployment.',
    );
  }
  userVerificationAPI.setIdentityToken(token);
};

/**
 * Clear the Salesforce Messaging session for the current user.
 *
 * This wraps `embeddedservice_bootstrap.userVerificationAPI.clearSession(clearAllTabsAndWindows)`
 * from the Salesforce docs.
 *
 * @param clearAllTabsAndWindows - When true, clears data across all tabs/windows.
 * @returns Promise that resolves when the session is cleared.
 *
 * @example
 * await clearSalesforceMessagingSession(true);
 */
export const clearSalesforceMessagingSession = async (
  clearAllTabsAndWindows = true,
): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  const userVerificationAPI =
    window.embeddedservice_bootstrap?.userVerificationAPI;

  if (!userVerificationAPI?.clearSession) {
    throw new Error(
      'Salesforce User Verification API is not available. Ensure Messaging is initialized and userVerification is enabled in your deployment.',
    );
  }

  await userVerificationAPI.clearSession(clearAllTabsAndWindows);
};
